// CompareTableView - Side-by-side comparison table + section views
import { useEffect, useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { getCellFromExplorerCache, getCellNormalized, getSignedPictureUrl } from '../../../modules/dataService.js'
import { compareCells, hiddenCells, removeFromCompare } from '../../../state/filterStore.js'
import { activeCompareSection, activeCompareView, basis } from '../../../state/globalSignals.js'
import { isDemoCatalogUnlocked } from '../../../state/demoLimitStore.js'
import CompareCellChip from '../CompareCellChip.jsx'
import CompareChemicalView from './CompareChemicalView.jsx'
import CompareElectricalView from './CompareElectricalView.jsx'
import CompareMechanicalView from './CompareMechanicalView.jsx'
import CompareQualityView from './CompareQualityView.jsx'
import CompareThermalView from './CompareThermalView.jsx'
import LockedMetricToken from '../../shared/LockedMetricToken.jsx'

// SVG Icons for categories
const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
)

const ElectricalIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
  </svg>
)

const MechanicalIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="3" y1="9" x2="21" y2="9"></line>
    <line x1="9" y1="21" x2="9" y2="9"></line>
  </svg>
)

const ThermalIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"></path>
  </svg>
)

const ChemicalIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 3h6l-6 18h6"></path>
    <path d="M6 12h12"></path>
  </svg>
)

const QualityIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
  </svg>
)

const ChevronIcon = ({ collapsed }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{ transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
  >
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
)

// Helper to get nested property value
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj)
}

// DEMO metric-tiering (Entscheidung 5): rows whose exact value the backend
// withholds for external DEMO users on a non-demo cell (see
// demo_field_policy._GATED_FIELDS — r_dc_mohm, r_ac_mohm, eta_dc_pct, and the
// DC25 thermal-under-load block). Compare doesn't get a per-cell gated_fields
// list from getCellNormalized (the single-cell detail path is the
// authoritative exact-within-quota view, unlike the explorer), so — matching
// the explorer's premium-field lock — DEMO tier renders these rows as a
// LockedMetricToken unconditionally, regardless of what the network payload
// happens to carry. Keyed by the same row.key used in getTableConfig() above.
// NOTE: 'thermal.delta_T_ch_25' is intentionally NOT gated — only the DC-side
// thermal-under-load fields are in _GATED_FIELDS today.
const DEMO_GATED_ROW_KEYS = {
  'electrical.R_dc_mOhm': 'electrical',
  'electrical.R_ac_mOhm': 'electrical',
  'electrical.eta_dc_pct': 'electrical',
  'thermal.W_loss_dc_25': 'thermal',
  'thermal.P_loss_dc_25': 'thermal',
  'thermal.delta_T_dc_25': 'thermal',
  // Quality EIS scalars: server-gated for demo (WS 1.4b) with no coarse band
  // channel on the detail shape, so they render as a PRO lock (not a band).
  'batch.EIS_spread_std_rel': 'quality',
  'aging.EIS_increase_rel': 'quality',
}

// DEMO metric-tiering (WS 1.4b): quality scalars the backend replaces with a
// coarse "low|medium|high" teaser band (the exact % is withheld). The band
// arrives inside the batch/aging category (see getCellNormalized), so we render
// a band chip instead of the raw value — never a bare "—". Keyed by row.key;
// `bandKey` is the cell-shape path getCellNormalized fills from the detail policy.
const DEMO_BAND_ROW_KEYS = {
  'batch.CAP_spread_std_rel': { bandKey: 'batch.CAP_spread_band', label: 'Spread', category: 'quality' },
  'aging.CAP_loss_rel': { bandKey: 'aging.CAP_loss_band', label: 'Loss', category: 'quality' },
}

// Country code mapping for flags
const countryToISO = {
  'China': 'CN', 'Japan': 'JP', 'South Korea': 'KR', 'Korea': 'KR',
  'USA': 'US', 'United States': 'US', 'Germany': 'DE', 'France': 'FR',
  'UK': 'GB', 'United Kingdom': 'GB', 'Italy': 'IT', 'Netherlands': 'NL',
  'Sweden': 'SE', 'Switzerland': 'CH', 'Austria': 'AT', 'Poland': 'PL',
  'Spain': 'ES', 'Belgium': 'BE', 'Czech Republic': 'CZ', 'Finland': 'FI',
  'Norway': 'NO', 'Denmark': 'DK', 'Canada': 'CA', 'Australia': 'AU',
  'Brazil': 'BR', 'India': 'IN'
}

// Table configuration with categories and rows (FULL FACT SHEET for Overview)
const getTableConfig = (currentBasis) => [
  {
    name: 'General Information',
    icon: <InfoIcon />,
    rows: [
      { label: 'Manufacturer', key: 'manufacturer', unit: '', format: (v) => v || 'N/A', highlight: false },
      { label: 'Model', key: 'name', unit: '', format: (v) => v || 'N/A', highlight: false },
      { label: 'Format', key: 'mechanical.format_group', unit: '', format: (v) => v || 'N/A', highlight: false },
      { label: 'Chemistry', key: 'chemical.main_chemistry', unit: '', format: (v) => v || 'N/A', highlight: false },
      { label: 'Year', key: 'date', unit: '', format: (v) => v ? v.split('-')[0] : 'N/A', highlight: false },
    ]
  },
  {
    name: 'Electrical Properties',
    icon: <ElectricalIcon />,
    rows: [
      { label: 'Energy (Abs)', key: 'electrical.E_c10_Wh', unit: 'Wh', format: (v) => v?.toFixed(1), highlight: false },
      { label: 'Energy (Grav)', key: 'electrical.E_grav_Wh_per_kg', unit: 'Wh/kg', format: (v) => v?.toFixed(0), highlight: 'max' },
      { label: 'Energy (Vol)', key: 'electrical.E_vol_Wh_per_L', unit: 'Wh/L', format: (v) => v?.toFixed(0), highlight: 'max' },
      { label: 'Peak Power (Abs)', key: 'electrical.P_peak_W', unit: 'W', format: (v) => v?.toFixed(1), highlight: false },
      { label: 'Peak Power (Grav)', key: 'electrical.P_grav_W_per_kg', unit: 'W/kg', format: (v) => v?.toFixed(0), highlight: 'max' },
      { label: 'Peak Power (Vol)', key: 'electrical.P_vol_W_per_L', unit: 'kW/L', format: (v) => v ? (v/1000).toFixed(1) : null, highlight: 'max' },
      { label: 'Capacity', key: 'electrical.C_c10_Ah', unit: 'Ah', format: (v) => v?.toFixed(2), highlight: false },
      { label: 'Current (Peak)', key: 'electrical.I_peak_A', unit: 'A', format: (v) => v?.toFixed(1), highlight: false },
      { label: 'Current (Cont)', key: 'electrical.I_cont_A', unit: 'A', format: (v) => v?.toFixed(1), highlight: false },
      { label: 'Voltage (Min)', key: 'electrical.V_min', unit: 'V', format: (v) => v?.toFixed(2), highlight: false },
      { label: 'Voltage (Nom)', key: 'electrical.V_mean', unit: 'V', format: (v) => v?.toFixed(2), highlight: false },
      { label: 'Voltage (Max)', key: 'electrical.V_max', unit: 'V', format: (v) => v?.toFixed(2), highlight: false },
      { label: 'DCIR', key: 'electrical.R_dc_mOhm', unit: 'mΩ', format: (v) => v?.toFixed(2), highlight: 'min' },
      { label: 'AC Impedance', key: 'electrical.R_ac_mOhm', unit: 'mΩ', format: (v) => v?.toFixed(2), highlight: 'min' },
    ]
  },
  {
    name: 'Mechanical Properties',
    icon: <MechanicalIcon />,
    rows: [
      { label: 'Mass', key: 'mechanical.mass_g', unit: 'g', format: (v) => v?.toFixed(1), highlight: false },
      { label: 'Volume', key: 'mechanical.vol_cell_cm3', unit: 'cm³', format: (v) => v?.toFixed(1), highlight: false },
      { label: 'Dimensions', key: 'mechanical.dimensions_text', unit: '', format: (v) => v || 'N/A', highlight: false },
    ]
  },
  {
    name: 'Thermal Properties',
    icon: <ThermalIcon />,
    rows: [
      { label: 'Thermal Energy Loss (DC)', key: 'thermal.W_loss_dc_25', unit: 'Wh', format: (v) => v?.toFixed(2), highlight: 'min' },
      { label: 'Thermal Power Loss (DC)', key: 'thermal.P_loss_dc_25', unit: 'W', format: (v) => v?.toFixed(2), highlight: 'min' },
      { label: 'Temperature Rise ΔT (DC)', key: 'thermal.delta_T_dc_25', unit: 'K', format: (v) => v?.toFixed(2), highlight: 'min' },
      { label: 'Temperature Rise ΔT (CH)', key: 'thermal.delta_T_ch_25', unit: 'K', format: (v) => v?.toFixed(2), highlight: 'min' },
    ]
  },
  {
    name: 'Chemical Composition',
    icon: <ChemicalIcon />,
    rows: [
      { label: 'Main Chemistry', key: 'chemical.main_chemistry', unit: '', format: (v) => v || 'N/A', highlight: false },
      { label: 'Cathode', key: 'chemical.cathode', unit: '', format: (v) => v || 'N/A', highlight: false },
      { label: 'Anode', key: 'chemical.anode', unit: '', format: (v) => v || 'N/A', highlight: false },
      { label: 'Housing Material', key: 'chemical.housing', unit: '', format: (v) => v || 'N/A', highlight: false },
    ]
  },
  {
    name: 'Quality & Post-Formation Loss',
    icon: <QualityIcon />,
    rows: [
      { label: 'DC Efficiency (25°C)', key: 'electrical.eta_dc_pct', unit: '%', format: (v) => v?.toFixed(1), highlight: 'max' },
      { label: 'Cell-to-Cell Variation (Cap)', key: 'batch.CAP_spread_std_rel', unit: '%', format: (v) => v != null ? (v * 100).toFixed(2) : null, highlight: 'min' },
      { label: 'Cell-to-Cell Variation (Res)', key: 'batch.EIS_spread_std_rel', unit: '%', format: (v) => v != null ? (v * 100).toFixed(2) : null, highlight: 'min' },
      { label: 'Capacity Change', key: 'aging.CAP_loss_rel', unit: '%', format: (v) => v != null ? (v < 0 ? `${(v * 100).toFixed(2)}` : v > 0 ? `+${(v * 100).toFixed(2)}` : `0.00`) : null, highlight: 'min' },
      { label: 'Resistance Increase', key: 'aging.EIS_increase_rel', unit: '%', format: (v) => v != null ? Math.abs(v * 100).toFixed(2) : null, highlight: 'min' },
    ]
  }
]

function CompareTableViewTable() {
  const [, setLocation] = useLocation()
  const [cellsData, setCellsData] = useState([])
  const [cellImages, setCellImages] = useState({})
  const [imageErrors, setImageErrors] = useState({})
  const [loading, setLoading] = useState(true)
  const [collapsedCategories, setCollapsedCategories] = useState({})

  const cellIds = compareCells.value
  const currentBasis = basis.value
  const currentSection = activeCompareSection.value

  const toggleCategory = (categoryName) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }))
  }

  console.log('[CompareTableView] Render:', { cellIds, cellCount: cellIds.length, currentSection, loading })

  useEffect(() => {
    if (!cellIds || cellIds.length === 0) {
      setCellsData([])
      setLoading(false)
      return
    }

    setLoading(true)
    Promise.all(cellIds.map(id => {
      // Table needs detail fields (V_min, V_max, dimensions_text) not in explorer cache.
      // If another compare view already loaded this cell, apiGet cache serves it instantly.
      const cached = getCellFromExplorerCache(id)
      if (cached?.electrical?.V_min !== undefined) return Promise.resolve(cached)
      return getCellNormalized(id, { fieldGating: 'demo' }) // Compare = browse: server-gate premium for demo
    }))
      .then(data => {
        setCellsData(data)
        setLoading(false)
        // Load images for all cells
        data.forEach(cell => {
          const cellId = cell.id || cell.product_id
          // Prefer the backend-gated thumbnail_url — only set when a 3D render
          // exists (like the grid). Cells without a render then fall back to the
          // placeholder instead of the signed-urls path, which returns a URL
          // unconditionally and 404s on the byte fetch → broken <img>.
          const gatedThumb = getCellFromExplorerCache(cellId)?.thumbnail_url
          if (gatedThumb) {
            setCellImages(prev => ({ ...prev, [cellId]: gatedThumb }))
            return
          }
          getSignedPictureUrl(cellId, 'website_picture_cell1.png')
            .then(signedUrl => {
              if (signedUrl) {
                setCellImages(prev => ({ ...prev, [cellId]: signedUrl }))
              }
            })
            .catch(err => console.warn('[CompareTableView] Image load failed:', err))
        })
      })
      .catch(err => {
        console.error('[CompareTableView] Error loading cells:', err)
        setLoading(false)
      })
  }, [cellIds.join(',')])

  // Filter visible cells (exclude hidden)
  const allCellsData = cellsData
  const visibleCellsData = allCellsData.filter(cell => {
    const cellId = cell.id || cell.product_id
    return !hiddenCells.value.includes(cellId)
  })

  if (loading) {
    return (
      <div className="compare-table-container">
        <div className="compare-table-header">
          <div className="compare-table-title">
            <div className="compare-table-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1"/>
                <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1"/>
                <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1"/>
                <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1"/>
              </svg>
            </div>
            <div className="compare-table-label">Cell Comparison</div>
          </div>
        </div>
        <div className="compare-table-content compare-table-loading">
          <div className="loading-spinner">
            <div className="loading-spinner__animation">
              <div className="loading-spinner__ring"></div>
              <div className="loading-spinner__core"></div>
            </div>
            <div className="loading-spinner__text">Loading cell data...</div>
          </div>
        </div>
      </div>
    )
  }

  // Auto-navigate back when compare becomes empty
  useEffect(() => {
    if (cellIds.length === 0) {
      const previousView = localStorage.getItem('previousCellsView') || 'cells-grid'
      console.log('[CompareTableView] Compare is empty, navigating back to:', previousView)

      // Build path based on previous view
      let backPath = '/explorer'
      if (previousView.startsWith('cells-')) {
        // cells-grid, cells-table, cells-ragone
        const viewType = previousView.replace('cells-', '') // 'grid', 'table', 'ragone'
        backPath = `/explorer?view=${viewType}`
      } else if (previousView.startsWith('benchmarking-')) {
        // benchmarking-score, benchmarking-my, benchmarking-free
        const plotType = previousView.replace('benchmarking-', '') // 'score', 'my', 'free'
        backPath = `/cell-selection?view=${plotType}`
      } else if (previousView === 'benchmarking') {
        backPath = '/cell-selection'
      }

      // Small delay to avoid flash of empty state
      setTimeout(() => {
        setLocation(backPath)
      }, 100)
    }
  }, [cellIds.length])

  if (cellIds.length === 0) {
    // Show empty state while redirecting (100ms delay above). Bug C fix: this
    // must key on the real empty cart (cellIds), never on allCellsData — a
    // full cart whose per-cell fetches all failed/gated/hit the daily limit
    // is NOT an empty cart and must never bounce the user back to Explorer.
    const previousView = localStorage.getItem('previousCellsView') || 'cells-grid'
    const isFromBenchmarking = previousView.includes('benchmarking')
    const backLabel = isFromBenchmarking ? 'Cell Selection' : 'Explorer'

    return (
      <div className="empty-state-modern">
        <div className="empty-state-card">
          <div className="empty-state-icon-wrapper">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              {/* Compare/Split icon */}
              <rect x="3" y="3" width="7" height="18" rx="1"></rect>
              <rect x="14" y="3" width="7" height="18" rx="1"></rect>
              <line x1="10.5" y1="8" x2="13.5" y2="8" strokeWidth="2" strokeLinecap="round"></line>
              <line x1="10.5" y1="12" x2="13.5" y2="12" strokeWidth="2" strokeLinecap="round"></line>
              <line x1="10.5" y1="16" x2="13.5" y2="16" strokeWidth="2" strokeLinecap="round"></line>
            </svg>
          </div>
          <h3 className="empty-state-title">Redirecting to {backLabel}...</h3>
          <p className="empty-state-description">
            No cells to compare. Taking you back to where you came from.
          </p>
        </div>
      </div>
    )
  }

  if (allCellsData.length === 0) {
    // Cart is NOT empty (cellIds.length > 0) but nothing loaded — e.g. every
    // cell is past today's DEMO deep-dive limit. Show the cells as locked
    // (WS 1.3 CompareCellChip, same lock affordance as the compare sidebar)
    // instead of bouncing to Explorer.
    return (
      <div className="compare-table-container">
        <div className="compare-table-header">
          <div className="compare-table-title">
            <div className="compare-table-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1"/>
                <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1"/>
                <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1"/>
                <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1"/>
              </svg>
            </div>
            <div className="compare-table-label">Cell Comparison</div>
          </div>
        </div>
        <div className="compare-table-content compare-table-locked">
          <p className="compare-locked-note">
            Some cells need a fresh deep dive — resets soon.
          </p>
          <div className="compare-locked-chips">
            {cellIds.map((cellId, index) => (
              <CompareCellChip
                key={cellId}
                cellId={cellId}
                index={index}
                onRemove={() => removeFromCompare(cellId)}
              />
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (visibleCellsData.length === 0) {
    return (
      <div className="compare-table-container">
        <div className="compare-table-header">
          <div className="compare-table-title">
            <div className="compare-table-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1"/>
                <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1"/>
                <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1"/>
                <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1"/>
              </svg>
            </div>
            <div className="compare-table-label">Cell Comparison</div>
          </div>
        </div>
        <div className="compare-table-content compare-table-empty">
          <div className="empty-state">
            <div className="empty-state-icon">👁️</div>
            <h3 className="empty-state-title">All Cells Hidden</h3>
            <p className="empty-state-description">
              Use the eye icon in the compare panel to show cells
            </p>
          </div>
        </div>
      </div>
    )
  }

  const tableConfig = getTableConfig(currentBasis)

  // Filter categories based on active section
  const getSectionLabel = (section) => {
    const labels = {
      'overview': 'Complete Overview',
      'electrical': 'Electrical Properties',
      'mechanical': 'Mechanical Properties',
      'chemical': 'Chemical Composition',
      'quality': 'Quality & Post-Formation Loss',
      'plots-3d': '3D Analysis'
    }
    return labels[section] || 'Comparison'
  }

  const filteredConfig = currentSection === 'overview'
    ? tableConfig
    : currentSection === 'electrical'
    ? [tableConfig.find(c => c.name === 'General'), tableConfig.find(c => c.name === 'Electrical')].filter(Boolean)
    : currentSection === 'mechanical'
    ? [tableConfig.find(c => c.name === 'General'), tableConfig.find(c => c.name === 'Mechanical')].filter(Boolean)
    : currentSection === 'chemical'
    ? [tableConfig.find(c => c.name === 'General'), tableConfig.find(c => c.name === 'Chemical')].filter(Boolean)
    : currentSection === 'quality'
    ? [tableConfig.find(c => c.name === 'General'), tableConfig.find(c => c.name === 'Quality & Post-Formation Loss')].filter(Boolean)
    : tableConfig

  return (
    <div className="compare-table-container" data-tour="compare-container">
      <div className="compare-table-header">
        <div className="compare-table-title">
          <div className="compare-table-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1"/>
              <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1"/>
              <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1"/>
              <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1"/>
            </svg>
          </div>
          <div className="compare-table-label">{getSectionLabel(currentSection)}</div>
          <div className="compare-table-count">{visibleCellsData.length} {visibleCellsData.length === 1 ? 'Cell' : 'Cells'}</div>
        </div>
      </div>
      <div className="compare-table-content" data-tour="compare-table-area">
        <div className="compare-table-scroll">
          <table className="compare-table">
          <thead className="sticky-header">
            <tr>
              <th className="property-col">Property</th>
              {visibleCellsData.map((cell, idx) => {
                const cellId = cell.id || cell.product_id
                return (
                <th key={cellId || idx} className="cell-col">
                  <div className="cell-header-card">
                    {/* Cell Image */}
                    <div className="cell-image-wrapper">
                      {cellImages[cellId] && !imageErrors[cellId] ? (
                        <img
                          src={cellImages[cellId]}
                          alt={`${cell.manufacturer} ${cell.name}`}
                          className="compare-cell-image"
                          onError={() => setImageErrors(prev => ({ ...prev, [cellId]: true }))}
                        />
                      ) : (
                        <div className="cell-image-placeholder">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="6" y="2" width="12" height="20" rx="2"></rect>
                            <line x1="6" y1="7" x2="18" y2="7"></line>
                            <line x1="6" y1="17" x2="18" y2="17"></line>
                          </svg>
                        </div>
                      )}
                    </div>
                    {/* Cell Info */}
                    <div className="cell-header-info">
                      <div className="cell-manufacturer">
                        {cell.manufacturer || 'Unknown'}
                      </div>
                      <div className="cell-name">
                        {cell.name || 'Unknown'}
                      </div>
                    </div>
                  </div>
                </th>
              )})}

            </tr>
          </thead>
          <tbody>
            {filteredConfig.map((category, catIdx) => {
              const isCollapsed = collapsedCategories[category.name]

              return (
              <>
                <tr
                  key={`cat-${catIdx}`}
                  className="category-header-row clickable"
                  onClick={() => toggleCategory(category.name)}
                >
                  <td colSpan={visibleCellsData.length + 1}>
                    <div className="category-header-content">
                      <span className="category-icon">{category.icon}</span>
                      <span className="category-name">{category.name}</span>
                      <ChevronIcon collapsed={isCollapsed} />
                    </div>
                  </td>
                </tr>
                {!isCollapsed && category.rows.map((row, rowIdx) => {
                  // DEMO metric-tiering (Entscheidung 5): premium electrical/
                  // thermal rows render as a locked token for external DEMO,
                  // never the raw measured value.
                  const gateCategory = DEMO_GATED_ROW_KEYS[row.key]
                  const rowIsGated = !!gateCategory && isDemoCatalogUnlocked()

                  // Quality teaser bands: coarse band chip for demo (exact % withheld).
                  const bandInfo = DEMO_BAND_ROW_KEYS[row.key]
                  const rowIsBanded = !!bandInfo && isDemoCatalogUnlocked()

                  // Extract values for all cells
                  const values = visibleCellsData.map(cell => {
                    const val = getNestedValue(cell, row.key)
                    return val != null && !isNaN(val) ? Number(val) : null
                  })

                  // Determine best and worst values (for highlighting)
                  let bestValue = null
                  let worstValue = null
                  const validValues = values.filter(v => v !== null)

                  if (row.highlight === 'max') {
                    bestValue = Math.max(...validValues)
                    worstValue = Math.min(...validValues)
                  } else if (row.highlight === 'min') {
                    bestValue = Math.min(...validValues)
                    worstValue = Math.max(...validValues)
                  }

                  return (
                    <tr key={`${catIdx}-${rowIdx}`}>
                      <td className="property-label">{row.label}</td>
                      {visibleCellsData.map((cell, cellIdx) => {
                        if (rowIsGated) {
                          return (
                            <td key={cellIdx} className="value-cell">
                              <LockedMetricToken gated unit={row.unit} category={gateCategory} />
                            </td>
                          )
                        }

                        if (rowIsBanded) {
                          const bandValue = getNestedValue(cell, bandInfo.bandKey)
                          return (
                            <td key={cellIdx} className="value-cell">
                              {bandValue ? (
                                <LockedMetricToken band={bandValue} label={bandInfo.label} category={bandInfo.category} />
                              ) : (
                                // No band came through (e.g. cell genuinely has no
                                // quality data) — still never a bare "—": show a lock.
                                <LockedMetricToken gated unit={row.unit} category={bandInfo.category} />
                              )}
                            </td>
                          )
                        }

                        const rawValue = getNestedValue(cell, row.key)
                        const numValue = values[cellIdx]
                        const isBest = row.highlight && numValue !== null && numValue === bestValue && validValues.length > 1
                        const isWorst = row.highlight && numValue !== null && numValue === worstValue && validValues.length > 1
                        const displayValue = row.format(rawValue, cell)

                        return (
                          <td
                            key={cellIdx}
                            className={`value-cell ${isBest ? 'highlight-best' : ''} ${isWorst ? 'highlight-worst' : ''}`}
                          >
                            {displayValue !== 'N/A' && displayValue != null ? (
                              <>
                                {displayValue}
                                {row.unit && <span className="unit"> {row.unit}</span>}
                              </>
                            ) : (
                              <span className="value-na">N/A</span>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </>
            )})}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  )
}

// Main CompareTableView component (wrapper)
export default function CompareTableView() {
  const activeSection = activeCompareSection.value
  const activeView = activeCompareView.value

  // Route to section-specific view components based on section and view
  if (activeSection === 'electrical') {
    return <CompareElectricalView view={activeView} />
  }

  if (activeSection === 'thermal') {
    return <CompareThermalView view={activeView} />
  }

  if (activeSection === 'mechanical') {
    return <CompareMechanicalView />
  }

  if (activeSection === 'quality') {
    return <CompareQualityView view={activeView} />
  }

  if (activeSection === 'chemical') {
    return <CompareChemicalView />
  }

  // Default: render table for overview section
  return <CompareTableViewTable />
}

// Export table component for use in section views
export { CompareTableViewTable }

